import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { leadsAPI } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { LogOut, Phone, Filter } from "lucide-react";
import { format } from "date-fns";

const getDisplayValue = (value) => {
  if (value && typeof value === "object" && "value" in value) {
    return value.value;
  }
  return value ?? "";
};

const STATUS_TABS = [
  { value: "rueckruf offen", label: "Rueckruf offen" },
  { value: "pruefen", label: "Pruefen" },
  { value: "qualifiziert", label: "Qualifiziert" },
  { value: "besichtigung terminiert", label: "Besichtigung" },
  { value: "abgelehnt", label: "Abgelehnt" },
  { value: "alle", label: "Alle" },
];

const OUTCOME_TABS = [
  { value: "alle", label: "Alle" },
  { value: "offen", label: "Offen" },
  { value: "deal", label: "Deal" },
  { value: "no_deal", label: "No Deal" },
  { value: "abgelehnt", label: "Abgelehnt" },
  { value: "vermittelt", label: "Vermittlung" },
];

const STATUS_COLORS = {
  "rueckruf offen": "bg-[#0A4F53] text-white",
  "pruefen": "bg-amber-100 text-amber-800",
  "qualifiziert": "bg-emerald-100 text-emerald-800",
  "besichtigung terminiert": "bg-blue-100 text-blue-800",
  "besichtigung": "bg-blue-100 text-blue-800",
  "abgelehnt": "bg-red-100 text-red-800",
};

const OUTCOME_COLORS = {
  offen: "border border-[#E2E4E9] bg-white text-[#5E646A]",
  deal: "border border-emerald-200 bg-emerald-50 text-emerald-700",
  no_deal: "border border-orange-200 bg-orange-50 text-orange-700",
  vermittelt: "border border-blue-200 bg-blue-50 text-blue-700",
  abgelehnt: "border border-red-200 bg-red-50 text-red-700",
};

function formatDate(dateStr) {
  try {
    return format(new Date(dateStr), "dd.MM.yyyy HH:mm");
  } catch {
    return dateStr || "-";
  }
}

export default function LeadListPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("rueckruf offen");
  const [outcomeTab, setOutcomeTab] = useState("alle");

  useEffect(() => {
    setLoading(true);
    leadsAPI
      .list(activeTab, outcomeTab !== "alle" ? outcomeTab : undefined)
      .then((res) => setLeads(res.data.results || []))
      .catch(() => setLeads([]))
      .finally(() => setLoading(false));
  }, [activeTab, outcomeTab]);

  return (
    <div className="h-screen flex flex-col" data-testid="lead-list-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E4E9] px-6 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-bold tracking-tight text-[#0A4F53]">
            ImmoBiber
          </h1>
          <span className="text-xs font-medium text-[#5E646A] bg-[#F0F2F5] px-2 py-0.5 rounded">
            Lead Engine
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-[#5E646A]" data-testid="user-name">
            {user?.name}
          </span>
          <span
            className="text-xs bg-[#F0F2F5] px-2 py-0.5 rounded text-[#5E646A] capitalize"
            data-testid="user-role"
          >
            {user?.role}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="text-[#5E646A] hover:text-[#1C1F22]"
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      {/* Filter Tabs */}
      <div className="bg-white border-b border-[#E2E4E9] px-6 py-2.5 shrink-0 space-y-2">
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-bold text-[#8A9198] uppercase tracking-widest w-20 shrink-0">
            Workflow
          </span>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList
              className="bg-[#F0F2F5] h-8"
              data-testid="status-tabs"
            >
              {STATUS_TABS.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  data-testid={`tab-${tab.value.replace(/\s/g, "-")}`}
                  className="text-xs font-medium data-[state=active]:bg-[#0A4F53] data-[state=active]:text-white"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-bold text-[#8A9198] uppercase tracking-widest w-20 shrink-0 flex items-center gap-1">
            <Filter className="h-2.5 w-2.5" />
            Ergebnis
          </span>
          <Tabs value={outcomeTab} onValueChange={setOutcomeTab}>
            <TabsList
              className="bg-[#F0F2F5] h-8"
              data-testid="outcome-tabs"
            >
              {OUTCOME_TABS.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  data-testid={`outcome-tab-${tab.value}`}
                  className="text-xs font-medium data-[state=active]:bg-[#5E646A] data-[state=active]:text-white"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div
            className="flex items-center justify-center h-40 text-[#5E646A] text-sm"
            data-testid="loading-indicator"
          >
            Leads werden geladen...
          </div>
        ) : leads.length === 0 ? (
          <div
            className="flex items-center justify-center h-40 text-[#5E646A] text-sm"
            data-testid="empty-state"
          >
            Keine Leads in dieser Kategorie
          </div>
        ) : (
          <Table data-testid="leads-table">
            <TableHeader>
              <TableRow className="bg-[#F8F9FA] hover:bg-[#F8F9FA]">
                <TableHead className="text-xs font-semibold text-[#5E646A] uppercase tracking-wider py-3 px-4">
                  Lead-ID
                </TableHead>
                <TableHead className="text-xs font-semibold text-[#5E646A] uppercase tracking-wider py-3 px-4">
                  Erstellt
                </TableHead>
                <TableHead className="text-xs font-semibold text-[#5E646A] uppercase tracking-wider py-3 px-4">
                  Quelle
                </TableHead>
                <TableHead className="text-xs font-semibold text-[#5E646A] uppercase tracking-wider py-3 px-4">
                  Kampagne
                </TableHead>
                <TableHead className="text-xs font-semibold text-[#5E646A] uppercase tracking-wider py-3 px-4">
                  Name
                </TableHead>
                <TableHead className="text-xs font-semibold text-[#5E646A] uppercase tracking-wider py-3 px-4">
                  Telefon
                </TableHead>
                <TableHead className="text-xs font-semibold text-[#5E646A] uppercase tracking-wider py-3 px-4">
                  Status
                </TableHead>
                <TableHead className="text-xs font-semibold text-[#5E646A] uppercase tracking-wider py-3 px-4">
                  Ergebnis
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.map((lead) => (
                <TableRow
                  key={lead.id}
                  className="cursor-pointer hover:bg-[#F8F9FA] border-b border-[#E2E4E9] transition-colors"
                  onClick={() => navigate(`/leads/${lead.id}`)}
                  data-testid={`lead-row-${lead.id}`}
                >
                  <TableCell className="py-3 px-4 text-sm font-medium text-[#0A4F53] tabular-nums">
                    {lead.lead_id}
                  </TableCell>
                  <TableCell className="py-3 px-4 text-sm text-[#1C1F22] tabular-nums">
                    {formatDate(lead.created_at)}
                  </TableCell>
                  <TableCell className="py-3 px-4 text-sm text-[#1C1F22]">
                    {getDisplayValue(lead.source)}
                  </TableCell>
                  <TableCell className="py-3 px-4 text-sm text-[#1C1F22]">
                    {lead.campaign}
                  </TableCell>
                  <TableCell className="py-3 px-4 text-sm font-medium text-[#1C1F22]">
                    {lead.name}
                  </TableCell>
                  <TableCell className="py-3 px-4 text-sm text-[#1C1F22] tabular-nums">
                    <span className="flex items-center gap-1.5">
                      <Phone className="h-3 w-3 text-[#5E646A]" />
                      {lead.phone}
                    </span>
                  </TableCell>
                  <TableCell className="py-3 px-4">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        STATUS_COLORS[getDisplayValue(lead.status)] || "bg-gray-100 text-gray-800"
                      }`}
                      data-testid={`lead-status-${lead.id}`}
                    >
                      {getDisplayValue(lead.status)}
                    </span>
                  </TableCell>
                  <TableCell className="py-3 px-4">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${
                        OUTCOME_COLORS[getDisplayValue(lead.lead_outcome)] || "bg-gray-100 text-gray-600"
                      }`}
                      data-testid={`lead-outcome-${lead.id}`}
                    >
                      {getDisplayValue(lead.lead_outcome) || "offen"}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
